import type { DriverInput, KartState, RaceState } from "./types";
import { getRaceClassProfile } from "./balance";
import { getShortcutDefinition, sampleShortcut, sampleTrack, wrappedDistanceDelta } from "./track";
import { getCourseEventSafeLateral } from "./courseEvents";
import { getDriverProfileForKart, type DriverItemTactics, type DriverProfileTraits } from "../driverProfiles";
import { CORKSCREW_TARGET_FRACTION, HICCUPS_MAX_TARGET_FRACTION, KEG_MAX_TARGET_DISTANCE } from "./items";

const HOUSE_TRAITS: Readonly<DriverProfileTraits> = {
  passAggression: 0.55,
  laneWidth: 2.8,
  laneTempo: 0.17,
  shortcutTendency: 0.48,
  driftCommitment: 0.56,
  itemPatience: 0.5,
  paceBias: 0,
  defenseTendency: 0.5,
  hazardAwareness: 0.62,
};

const HOUSE_ITEM_TACTICS: Readonly<DriverItemTactics> = {
  kegRange: 76,
  foamThreatRange: 30,
  shieldPlace: 4,
  comebackPlace: 7,
  boostCurvature: 0.5,
};

function getDecisionTraits(kart: KartState): Readonly<DriverProfileTraits> {
  return getDriverProfileForKart(kart)?.traits ?? HOUSE_TRAITS;
}

function getItemTactics(kart: KartState): Readonly<DriverItemTactics> {
  return getDriverProfileForKart(kart)?.itemTactics ?? HOUSE_ITEM_TACTICS;
}

function wrapAngle(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

export type AiRacecraftMode = "cruise" | "pass" | "queue" | "defend";

export interface AiRacecraftDecision {
  mode: AiRacecraftMode;
  laneTarget: number | null;
  brake: number;
  throttlePenalty: number;
  rivalId: string | null;
}

const CRUISE_DECISION: AiRacecraftDecision = { mode: "cruise", laneTarget: null, brake: 0, throttlePenalty: 0, rivalId: null };

function availableHazard(race: RaceState, distance: number, lateral: number, radius: number): boolean {
  return race.hazards.some((hazard) => {
    if (hazard.respawnAt > race.elapsed || (hazard.expiresAt !== undefined && hazard.expiresAt <= race.elapsed)) return false;
    const gap = wrappedDistanceDelta(distance, hazard.distance, race.trackId);
    return gap > -3 && gap < 30 && Math.abs(hazard.lateral - lateral) < hazard.radius + radius;
  });
}

/**
 * Pick a stable pack-racing line before steering is calculated. This remains a
 * pure decision helper so local, authoritative AI and Twitch fallback all use
 * identical racecraft and deterministic tests can inspect the choice directly.
 */
export function getAiRacecraftDecision(kart: KartState, race: RaceState, _time: number): AiRacecraftDecision {
  if (kart.airborne || kart.spinTime > 0 || kart.surface === "offroad" || kart.recoveryTime > 0 || kart.finishedAt !== null) return CRUISE_DECISION;
  const tuning = getRaceClassProfile(race.raceClass);
  const traits = getDecisionTraits(kart);
  const directive = kart.directiveUntil > race.elapsed ? kart.twitchDirective : "steady";
  const decisionAggression = directive === "attack" ? 1 : directive === "defend" ? 0 : traits.passAggression;
  const aggression = Math.max(0.72, Math.min(1.26, tuning.aiSkill + (decisionAggression - 0.5) * 0.32));
  const passRange = 13 + aggression * 6 + decisionAggression * 8;
  const rivals = race.karts.filter((rival) => rival.id !== kart.id && rival.finishedAt === null);
  const blocker = rivals
    .map((rival) => ({ rival, gap: rival.distance - kart.distance, closing: kart.speed - rival.speed }))
    .filter(({ rival, gap, closing }) => gap > 2.2 && gap < passRange && Math.abs(rival.lateral - kart.lateral) < 4.7 && (closing > 0.35 || gap < 8.5))
    .sort((a, b) => a.gap - b.gap || a.rival.id.localeCompare(b.rival.id))[0];

  if (blocker && directive !== "defend") {
    const passOffset = 3.05 + aggression * 0.55;
    const candidates = ([-1, 1] as const).map((side) => {
      const lane = Math.max(-6.2, Math.min(6.2, blocker.rival.lateral + side * passOffset));
      const occupied = rivals.some((rival) => {
        if (rival.id === blocker.rival.id) return false;
        const relative = rival.distance - kart.distance;
        return relative > -5.5 && relative < blocker.gap + 10 && Math.abs(rival.lateral - lane) < 2.65;
      });
      const hazardous = availableHazard(race, kart.distance, lane, 1.15);
      const deterministicPreference = (Number(kart.id.replace(/\D/g, "")) || 0) % 2 === (side > 0 ? 1 : 0) ? 0 : 0.08;
      return { side, lane, blocked: occupied || hazardous, score: (occupied ? 20 : 0) + (hazardous ? 14 : 0) + Math.abs(lane) * 0.025 + deterministicPreference };
    }).sort((a, b) => a.score - b.score || a.side - b.side);
    const selected = candidates[0];
    if (!selected.blocked) return { mode: "pass", laneTarget: selected.lane, brake: 0, throttlePenalty: 0, rivalId: blocker.rival.id };
    const closing = Math.max(0, blocker.closing);
    const caution = Math.max(0.72, 1.36 - aggression * 0.42);
    return {
      mode: "queue",
      laneTarget: blocker.rival.lateral,
      brake: Math.min(0.62, (0.16 + closing / 46 + Math.max(0, 8 - blocker.gap) / 24) * caution),
      throttlePenalty: Math.min(0.24, 0.08 + closing / 65),
      rivalId: blocker.rival.id,
    };
  }

  const pursuer = rivals
    .map((rival) => ({ rival, gap: kart.distance - rival.distance, closing: rival.speed - kart.speed }))
    .filter(({ gap, closing }) => gap > 1.5 && gap < (directive === "defend" ? 12 : 9.5) && closing > -1.5)
    .sort((a, b) => a.gap - b.gap || a.rival.id.localeCompare(b.rival.id))[0];
  const defenseThreshold = 0.5 + ((((kart.lap + Number(kart.id.replace(/\D/g, ""))) % 3) - 1) * 0.12);
  if (pursuer && directive !== "attack" && (directive === "defend" || kart.place <= 3 && traits.defenseTendency >= defenseThreshold)) {
    const strength = directive === "defend" ? 0.82 : 0.58;
    return {
      mode: "defend",
      laneTarget: Math.max(-4.2, Math.min(4.2, pursuer.rival.lateral * strength)),
      brake: 0,
      throttlePenalty: 0,
      rivalId: pursuer.rival.id,
    };
  }
  return CRUISE_DECISION;
}

export type AiItemDecisionReason = "none" | "target-ahead" | "rear-threat" | "field-defense" | "comeback" | "boost-window" | "timeout";

export interface AiItemDecision {
  use: boolean;
  reason: AiItemDecisionReason;
  nearestAheadGap: number | null;
  nearestBehindGap: number | null;
}

function getUpcomingCurvature(kart: KartState, race: RaceState): number {
  const nearFrame = sampleTrack(kart.distance + 6, race.trackId);
  const farFrame = sampleTrack(kart.distance + 22, race.trackId);
  return Math.abs(wrapAngle(Math.atan2(farFrame.tangent.x, farFrame.tangent.z) - Math.atan2(nearFrame.tangent.x, nearFrame.tangent.z)));
}

/** Pure, profile-driven item timing for autonomous CPU drivers. */
export function getAiItemDecision(kart: KartState, race: RaceState, curvature: number): AiItemDecision {
  if (!kart.heldItem) return { use: false, reason: "none", nearestAheadGap: null, nearestBehindGap: null };
  const traits = getDecisionTraits(kart);
  const tactics = getItemTactics(kart);
  const ahead = race.karts
    .filter((rival) => rival.id !== kart.id && rival.finishedAt === null && rival.distance > kart.distance)
    .reduce((best, rival) => Math.min(best, rival.distance - kart.distance), Number.POSITIVE_INFINITY);
  const behind = race.karts
    .filter((rival) => rival.id !== kart.id && rival.finishedAt === null && rival.distance < kart.distance)
    .reduce((best, rival) => Math.min(best, kart.distance - rival.distance), Number.POSITIVE_INFINITY);
  const nearestAheadGap = Number.isFinite(ahead) ? ahead : null;
  const nearestBehindGap = Number.isFinite(behind) ? behind : null;
  const heldFor = Math.max(0, race.elapsed - kart.itemHeldAt);
  const timeout = 3 + traits.itemPatience * 6;
  const result = (use: boolean, reason: AiItemDecisionReason): AiItemDecision => ({ use, reason, nearestAheadGap, nearestBehindGap });
  if (kart.heldItem === "goggles") {
    const incomingProjectile = race.projectiles.some((projectile) => projectile.targetId === kart.id
      || Math.hypot(projectile.x - kart.x, projectile.z - kart.z) < 9);
    const closeRival = (nearestAheadGap !== null && nearestAheadGap < 10) || (nearestBehindGap !== null && nearestBehindGap < 10);
    return incomingProjectile || closeRival
      ? result(true, "field-defense")
      : heldFor > timeout ? result(true, "timeout") : result(false, "none");
  }
  if (kart.heldItem === "round") return result(true, "field-defense");
  if (kart.heldItem === "corkscrew") {
    const target = race.karts.some((rival) => rival.id !== kart.id && rival.finishedAt === null && rival.ghostTime <= 0
      && rival.heldItem !== null && rival.distance > kart.distance
      && rival.distance - kart.distance <= race.trackLength * CORKSCREW_TARGET_FRACTION);
    return target && race.elapsed >= 4
      ? result(true, "target-ahead") : heldFor > timeout ? result(true, "timeout") : result(false, "none");
  }
  if (kart.heldItem === "spill") return nearestBehindGap !== null && nearestBehindGap < tactics.foamThreatRange * 1.2
    ? result(true, "rear-threat") : heldFor > timeout ? result(true, "timeout") : result(false, "none");
  if (kart.heldItem === "bottle" || kart.heldItem === "hiccups") {
    // Hiccups needs a nearby same-deck target. Bottle is a straight bank shot,
    // so autonomous drivers wait for both a nearby rival and a readable aim
    // window instead of receiving hidden homing assistance.
    const targetRange = race.trackLength * HICCUPS_MAX_TARGET_FRACTION;
    const sameDeckAhead = race.karts
      .filter((rival) => rival.id !== kart.id && rival.finishedAt === null && rival.distance > kart.distance && Math.abs(rival.y - kart.y) <= 2.25)
      .reduce((best, rival) => Math.min(best, rival.distance - kart.distance), Number.POSITIVE_INFINITY);
    const aimWindow = kart.heldItem === "hiccups" || curvature < 0.24;
    return Number.isFinite(sameDeckAhead) && sameDeckAhead <= targetRange && race.elapsed >= 4 && aimWindow
      ? result(true, "target-ahead")
      : heldFor > timeout ? result(true, "timeout") : result(false, "none");
  }
  if (kart.heldItem === "lime-wedge") {
    // Lime is the recovery button and is deliberately valid during a spin or
    // Hiccups debuff. Preserve it briefly when the kart is healthy, but never
    // let autonomous drivers hoard it forever.
    return kart.spinTime > 0 || kart.hiccupTime > 0
      ? result(true, "field-defense")
      : heldFor > timeout ? result(true, "timeout") : result(false, "none");
  }
  if (kart.heldItem === "keg") return nearestAheadGap !== null && nearestAheadGap < Math.min(tactics.kegRange, KEG_MAX_TARGET_DISTANCE) && race.elapsed >= 4
    ? result(true, "target-ahead") : heldFor > timeout ? result(true, "timeout") : result(false, "none");
  if (kart.heldItem === "foam") return nearestBehindGap !== null && nearestBehindGap < tactics.foamThreatRange
    ? result(true, "rear-threat") : heldFor > timeout ? result(true, "timeout") : result(false, "none");
  if (kart.heldItem === "shield") return nearestBehindGap !== null && nearestBehindGap < tactics.foamThreatRange * 0.8
    ? result(true, "rear-threat") : kart.place >= tactics.shieldPlace ? result(true, "field-defense") : heldFor > timeout ? result(true, "timeout") : result(false, "none");
  if (kart.heldItem === "last-orders") return kart.place >= tactics.comebackPlace
    ? result(true, "comeback") : heldFor > 4 + traits.itemPatience * 6 ? result(true, "timeout") : result(false, "none");
  if (kart.heldItem === "turbo" || kart.heldItem === "six-pack") return curvature < tactics.boostCurvature && heldFor > traits.itemPatience * 3
    ? result(true, "boost-window") : heldFor > timeout ? result(true, "timeout") : result(false, "none");
  return result(false, "none");
}

export function getAiBehaviorDiagnostics(kart: KartState, race: RaceState, time = race.elapsed) {
  const profile = getDriverProfileForKart(kart);
  const traits = getDecisionTraits(kart);
  return {
    profileId: profile?.id ?? "house",
    signature: profile?.signature ?? "Balanced pack racing",
    racecraft: getAiRacecraftDecision(kart, race, time),
    item: getAiItemDecision(kart, race, getUpcomingCurvature(kart, race)),
    shortcutTendency: traits.shortcutTendency,
    driftCommitment: traits.driftCommitment,
    defenseTendency: traits.defenseTendency,
    hazardLookAhead: 16 + traits.hazardAwareness * 18,
    itemTactics: { ...getItemTactics(kart) },
  };
}

export function getAiInput(kart: KartState, race: RaceState, time: number): DriverInput {
  const tuning = getRaceClassProfile(race.raceClass);
  const traits = getDecisionTraits(kart);
  const seed = Number(kart.id.replace(/\D/g, "")) || 1;
  const explicitLane = kart.laneBiasUntil > race.elapsed;
  let laneTarget = explicitLane ? kart.laneBias : Math.sin(time * traits.laneTempo + seed * 1.73) * traits.laneWidth;
  const lookAhead = (9 + kart.speed * 0.7) * tuning.aiSkill;
  const shortcut = getShortcutDefinition(race.trackId);
  const shortcutApproach = shortcut ? wrappedDistanceDelta(kart.distance, shortcut.startDistance, race.trackId) : Infinity;
  const shortcutThreshold = 0.5 + ((((kart.lap + race.trackId.length) % 3) - 1) * 0.12);
  const courseEventSafeLateral = !explicitLane ? getCourseEventSafeLateral(kart, race) : null;
  const approachShortcut = !explicitLane && courseEventSafeLateral === null && traits.shortcutTendency >= shortcutThreshold && shortcutApproach > 0 && shortcutApproach < 16;
  const takeShortcut = shortcut !== null && courseEventSafeLateral === null && (kart.onShortcut || (approachShortcut && shortcutApproach <= 4));
  const hazardLookAhead = 16 + traits.hazardAwareness * 18;
  const hazardAhead = race.hazards.find((hazard) => hazard.respawnAt <= race.elapsed && wrappedDistanceDelta(kart.distance, hazard.distance, race.trackId) > 0 && wrappedDistanceDelta(kart.distance, hazard.distance, race.trackId) < hazardLookAhead);
  if (hazardAhead && !takeShortcut && kart.laneBiasUntil <= race.elapsed) {
    const avoidanceLane = 4.8 + traits.hazardAwareness * 1.2;
    laneTarget = hazardAhead.lateral >= 0 ? -avoidanceLane : avoidanceLane;
  }
  const racecraft = getAiRacecraftDecision(kart, race, time);
  if (!explicitLane && !hazardAhead && !takeShortcut && racecraft.laneTarget !== null) laneTarget = racecraft.laneTarget;
  // A course event is a visible, shared obstacle.  Let autonomous AI take the
  // safe lane even when passing/racecraft or a hazard would choose another;
  // an explicit Twitch laneBias remains authoritative by construction.
  if (courseEventSafeLateral !== null) laneTarget = courseEventSafeLateral;
  const shortcutProgress = shortcut ? Math.max(0, Math.min(1, (kart.distance - shortcut.startDistance) / (shortcut.endDistance - shortcut.startDistance))) : 0;
  const shortcutTarget = shortcut && takeShortcut ? sampleShortcut(Math.min(1, (kart.onShortcut ? shortcutProgress : 0) + Math.max(0.16, lookAhead / shortcut.length)), race.trackId) : null;
  const targetFrame = sampleTrack(shortcut && takeShortcut ? shortcut.endDistance + 8 : shortcut && approachShortcut ? shortcut.startDistance : kart.distance + lookAhead, race.trackId);
  const targetX = shortcutTarget ? shortcutTarget.position.x : shortcut && approachShortcut ? shortcut.start.x : targetFrame.position.x + targetFrame.normal.x * laneTarget;
  const targetZ = shortcutTarget ? shortcutTarget.position.z : shortcut && approachShortcut ? shortcut.start.z : targetFrame.position.z + targetFrame.normal.z * laneTarget;
  const desiredYaw = Math.atan2(targetX - kart.x, targetZ - kart.z);
  const steerError = wrapAngle(desiredYaw - kart.yaw);
  const steer = Math.max(-1, Math.min(1, steerError * 1.9 * tuning.aiSkill));
  const progress = kart.distance / race.trackLength;
  const player = race.karts.find((candidate) => candidate.controller === "human");
  const activeField = race.karts.filter((candidate) => candidate.finishedAt === null);
  const leaderDistance = Math.max(...activeField.map((candidate) => candidate.distance));
  const leaderGap = Math.max(0, leaderDistance - kart.distance);
  const fieldCatchup = Math.min(0.11, leaderGap / 360) * tuning.rubberBandScale;
  const humanCatchup = player ? Math.max(-0.035, Math.min(0.045, (player.distance - kart.distance) / 520)) * tuning.rubberBandScale : 0;
  const catchup = fieldCatchup + humanCatchup;
  const directive = kart.directiveUntil > race.elapsed ? kart.twitchDirective : "steady";
  const directiveThrottle = directive === "boost" ? 0.1 : directive === "attack" ? 0.035 : directive === "defend" ? -0.04 : 0;
  const classPace = (tuning.aiSkill - 1) * 0.18;
  const throttle = Math.max(0.58, Math.min(1, 0.87 + classPace + catchup + directiveThrottle + traits.paceBias - (!explicitLane && !hazardAhead && !takeShortcut ? racecraft.throttlePenalty : 0)));
  const curvature = getUpcomingCurvature(kart, race);
  const driftCommitment = directive === "attack" ? 1 : traits.driftCommitment;
  const driftThreshold = (0.29 - driftCommitment * 0.11) / tuning.aiSkill;
  const driftCorner = curvature > driftThreshold && Math.abs(steer) > Math.max(0.24, 0.38 - driftCommitment * 0.1);
  const itemDecision = getAiItemDecision(kart, race, curvature);
  // Twitch inventory is viewer-owned.  The pickup notice is the viewer's
  // command window, so never consume it behind chat's back; CPU drivers keep
  // the same profile-driven autonomous timing as before.
  const autonomousUse = kart.controller !== "twitch" && Boolean(kart.heldItem) && itemDecision.use;
  const shouldUseItem = Boolean(kart.heldItem) && (kart.pendingItemUse || autonomousUse);
  return {
    throttle,
    brake: Math.max(curvature > (directive === "attack" ? 0.68 : 0.58) / tuning.aiSkill && kart.speed > 38 ? 0.34 : 0, !explicitLane && !hazardAhead && !takeShortcut ? racecraft.brake : 0),
    steer: Math.max(-1, Math.min(1, steer + (directive === "defend" ? Math.sin(time * 2 + seed) * 0.08 : 0))),
    drift: driftCorner && progress > -0.2 && directive !== "defend",
    useSeq: shouldUseItem ? kart.lastUseSeq + 1 : kart.lastUseSeq,
  };
}

export function getAiPaceAssist(kart: KartState, race: RaceState): number {
  const activeField = race.karts.filter((candidate) => candidate.finishedAt === null);
  if (activeField.length < 2) return 0;
  const leaderDistance = Math.max(...activeField.map((candidate) => candidate.distance));
  const lastDistance = Math.min(...activeField.map((candidate) => candidate.distance));
  const gap = Math.max(0, leaderDistance - kart.distance);
  const tuning = getRaceClassProfile(race.raceClass);
  const catchup = Math.min(0.075, Math.max(0, gap - 32) / 520) * tuning.rubberBandScale;
  const leaderRestraint = gap < 2 ? Math.min(0.025, Math.max(0, leaderDistance - lastDistance - 90) / 900) * tuning.rubberBandScale : 0;
  return catchup - leaderRestraint;
}
